import { FC, useState } from 'react';
import { generatePath } from 'react-router-dom';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { ButtonLink } from '@savgroup-front-common/core/atoms/button';
import Button from '@savgroup-front-common/core/atoms/button/Button';
import { MODAL_SIZES } from '@savgroup-front-common/core/atoms/modal';
import Modal from '@savgroup-front-common/core/atoms/modal/Modal';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { PRICING_PLAN, PRICING_PLAN_STATE } from '@savgroup-front-common/types';

import { BASES } from '../../../../../../constants/routing';
import { useAppContext } from '../../../../../app/App.context';
import { useChoosePlanContext } from '../../../ChoosePlanDrawer.context';
import { PlanDto } from '../../ChoosePlan.types';

import { ConfirmPushToProductionModalContent } from './ConfirmPushToProductionModalContent/ConfirmPushToProductionModalContent';
import messages from './messages';

interface ChoosePlanButtonProps {
  plan: PlanDto;
  isLoading: boolean;
  onActivatePricingPlan: () => void;
}

export const ChoosePlanButton: FC<ChoosePlanButtonProps> = ({
  plan,
  isLoading,
  onActivatePricingPlan,
}) => {
  const { handleDrawerClose } = useChoosePlanContext();
  const { selectedSeller } = useAppContext();
  const [
    isConfirmPushToProductionModalOpen,
    setIsConfirmPushToProductionModalOpen,
  ] = useState(false);

  const handleOpenConfirmPushToProductionModal = () => {
    setIsConfirmPushToProductionModalOpen(true);
  };

  const handleCloseConfirmPushToProductionModal = () => {
    setIsConfirmPushToProductionModalOpen(false);
  };

  const isDemoWithNoPricingPlan =
    selectedSeller?.pricingPlan === PRICING_PLAN.NONE;

  const hasActivePlan =
    selectedSeller?.pricingPlan !== PRICING_PLAN.NONE &&
    selectedSeller?.pricingPlanState !== PRICING_PLAN_STATE.CHURN;

  const isAlreadySubscribedToThisPlan =
    hasActivePlan && selectedSeller?.pricingPlan === plan.pricingPlan;

  const planButtonMessage =
    plan.pricingPlan === PRICING_PLAN.ADVANCED
      ? messages.getAdvancedStarted
      : messages.getStarted;

  return (
    <>
      {!isDemoWithNoPricingPlan && (
        <ButtonLink
          isLoading={isLoading}
          secondary
          disabled={isAlreadySubscribedToThisPlan}
          href={generatePath(BASES.FROM_SCRATCH)}
          onClick={() => {
            handleDrawerClose();
            onActivatePricingPlan();
          }}
        >
          <SafeFormattedMessageWithoutSpread
            message={
              isAlreadySubscribedToThisPlan
                ? messages.isActive
                : planButtonMessage
            }
          />
        </ButtonLink>
      )}

      {isDemoWithNoPricingPlan && (
        <Button
          type={BUTTON_TYPES.BUTTON}
          primary
          onClick={() => {
            handleOpenConfirmPushToProductionModal();
          }}
          fluid
          isLoading={isLoading}
        >
          <SafeFormattedMessageWithoutSpread message={messages.getStarted} />
        </Button>
      )}

      <Modal
        isOpen={isConfirmPushToProductionModalOpen}
        size={MODAL_SIZES.SMALL}
      >
        <ConfirmPushToProductionModalContent
          onClose={handleCloseConfirmPushToProductionModal}
          plan={plan}
        />
      </Modal>
    </>
  );
};
